import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
    dictationCommandsFor,
    type DictationLanguage,
} from "../../domain/dictationText";
import { Tooltip } from "./Tooltip";

type Props = {
    language: DictationLanguage;
};

/**
 * What to say to get punctuation while dictating, next to the dictate button.
 *
 * Phrases for the same mark share a line ("punto y seguido" and "punto final"
 * both end a sentence), so the list stays short enough to read at a glance.
 */
export default function DictationPunctuationHint({ language }: Props) {
    const { t } = useTranslation();

    const byMark = new Map<string, string[]>();
    for (const { mark, say } of dictationCommandsFor(language)) {
        byMark.set(mark, [...(byMark.get(mark) ?? []), say]);
    }

    return (
        <Tooltip
            position="bottom"
            content={
                <div className="space-y-2 text-left">
                    <p className="font-semibold">{t("punctuationHelp.title")}</p>
                    <ul className="space-y-1">
                        {[...byMark].map(([mark, phrases]) => (
                            <li key={mark} className="flex gap-2">
                                <span className="w-4 shrink-0 text-center font-mono">{mark}</span>
                                <span className="font-normal">
                                    {phrases.map((phrase) => `«${phrase}»`).join(", ")}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <p className="font-normal text-gray-300">{t("punctuationHelp.note")}</p>
                </div>
            }
        >
            <button
                type="button"
                aria-label={t("punctuationHelp.label")}
                className="flex h-8 w-8 items-center justify-center rounded-full text-content-tertiary transition-colors hover:text-accent-text"
            >
                <Info className="h-4 w-4" />
            </button>
        </Tooltip>
    );
}
